import { useEffect } from 'react';
import config from '@/seo.config.json';

/**
 * What search engines and social cards are told about each page.
 *
 * Kept in one JSON file because two very different things need the same answer: this
 * module, which updates the head as somebody moves between routes, and the build
 * script, which writes a real HTML file per route so the answer is in the response
 * rather than only appearing after the bundle has run. Two copies of a title drift,
 * and the one that drifts is always the one Google reads.
 */

export interface RouteSeo {
  path: string;
  title: string;
  description: string;
  indexable: boolean;
}

/**
 * The canonical origin, overridable at build time.
 *
 * Canonical and Open Graph URLs have to be absolute, so a wrong value here points
 * every search engine and every shared link at a domain that is not this one. The
 * default is the live site; a preview deployment sets VITE_PUBLIC_SITE_URL to its own
 * origin so it does not claim to be production.
 */
export const SITE = {
  ...config.site,
  url: (import.meta.env.VITE_PUBLIC_SITE_URL ?? config.site.url).replace(/\/$/, ''),
};

/** The page's main heading, shared with the copy the build writes into the HTML. */
export const SEO_H1: string = config.h1;

export const ROUTE_SEO: RouteSeo[] = config.routes.map((route) => ({
  path: route.path,
  title: route.title,
  description: route.description,
  indexable: route.indexable,
}));

export const seoFor = (path: string): RouteSeo | undefined =>
  ROUTE_SEO.find((route) => route.path === path);

/** Set or replace one meta/link tag, matched by the attribute that identifies it. */
function setTag(selector: string, create: () => HTMLElement, content: string, attribute: string): void {
  let element = document.head.querySelector<HTMLElement>(selector);
  if (!element) {
    element = create();
    document.head.appendChild(element);
  }
  element.setAttribute(attribute, content);
}

/**
 * Point the head at this route.
 *
 * The build writes the same values into the HTML of each prerendered route, so this
 * matters for the case the build cannot cover: somebody who arrived on one page and
 * navigated to another without a reload, and anything that reads the live DOM.
 */
export function applySeo(route: RouteSeo, url: string): void {
  document.title = route.title;

  setTag('meta[name="description"]', () => {
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'description');
    return meta;
  }, route.description, 'content');

  setTag('link[rel="canonical"]', () => {
    const link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    return link;
  }, url, 'href');

  setTag('meta[name="robots"]', () => {
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'robots');
    return meta;
  }, route.indexable ? 'index, follow, max-image-preview:large' : 'noindex, follow', 'content');

  const og: Array<[string, string]> = [
    ['og:title', route.title],
    ['og:description', route.description],
    ['og:url', url],
  ];

  for (const [property, content] of og) {
    setTag(`meta[property="${property}"]`, () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', property);
      return meta;
    }, content, 'content');
  }
}

/**
 * Keep the head pointed at the route being shown.
 *
 * Replaces useDocumentTitle on the public pages, which set a title and nothing else —
 * so every one of them shared the shell's description, canonical and social card.
 */
export function useSeo(path: string): void {
  useEffect(() => {
    const route = seoFor(path);
    if (!route) return;

    applySeo(route, `${SITE.url.replace(/\/$/, '')}${path}`);
  }, [path]);
}
